import { BobEvent } from "../stores/BobEvent";
import { BobsWorkspace } from "../stores/BobsWorkspace";
import { TWO_FACTOR_INTERVALS } from "./constants";

// map minutes → human-readable label (e.g. 720 → "12 hours")
const intervalLabelFor = (minutes: number): string => {
  const found = TWO_FACTOR_INTERVALS.find((i) => Number(i.value) === minutes);
  if (found) return found.label;

  // fallback formatting
  if (minutes % 1440 === 0) return `${minutes / 1440} days`;
  if (minutes % 60 === 0) return `${minutes / 60} hours`;
  return `${minutes} minutes`;
};

export const initEvents = (bobsWorkspace: BobsWorkspace) => {
  // balance-based thresholds (sats)
  const twoFactorBalanceThreshold = 100_000;
  const highBalanceThreshold = 500_000;
  const longTimeoutThreshold = 720; // 12 h or longer is considered “long”

  let needsTwoFactorEnableWarning = false;
  let needsHighBalanceWarning = false;

  // scan every Bob for balance-related alerts
  for (const bob of bobsWorkspace.bobs) {
    if (
      bob.safeBalanceInSatoshis > twoFactorBalanceThreshold &&
      !bobsWorkspace.accountIsTwoFactorProtected
    ) {
      needsTwoFactorEnableWarning = true;
    }
    if (bob.safeBalanceInSatoshis > highBalanceThreshold) {
      needsHighBalanceWarning = true;
    }
  }

  // suggest enabling 2FA
  if (needsTwoFactorEnableWarning) {
    bobsWorkspace.events.push(
      new BobEvent({
        title: "Two-Factor Authentication Recommended",
        message:
          "Your vault balance is high enough that we recommend enabling Two-Factor Authentication for your account.",
        kind: "platform-alert",
      })
    );
  }

  // warn if 2FA timeout is too long (≥ 12 h)
  if (bobsWorkspace.accountIsTwoFactorProtected) {
    const intervalMinutes = bobsWorkspace.accountTwoFactorIntervalMinutes ?? 0;
    if (intervalMinutes >= longTimeoutThreshold) {
      const label = intervalLabelFor(intervalMinutes);
      bobsWorkspace.events.push(
        new BobEvent({
          title: "Two-Factor Timeout",
          message: `You have Two-Factor Authentication set up, but, once you log in with your two-factor code, Rizful only requests a new code every ${label}. Go to Settings → Adjust 2FA Timeout and shorten this interval to increase your protection.`,
          kind: "platform-alert",
        })
      );
    }
  }

  // advise moving excess funds to cold storage
  if (needsHighBalanceWarning) {
    bobsWorkspace.events.push(
      new BobEvent({
        title: "High Balance Warning",
        message:
          "You have a high balance in your vaults. Please withdraw funds you don't intend to spend soon to cold storage.",
        kind: "platform-alert",
      })
    );
  }
};
